import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import ReactJsonView from 'react-json-view';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faAmazon } from '@fortawesome/free-brands-svg-icons';
import { getBondColor } from '../../../../../../../../lib/ui/colorUtils.js';
import '../MqttEvent/MqttEvent.css';

// ── Shared style maps ─────────────────────────────────────────────────────────

// Smart integrations: dark fill + vivid brand border, matching the MQTT client badges
const CLIENT_STYLES = {
    GOOGLE: { background: '#06180e', color: '#a7f3c9', border: '2px solid #4ade80' },                  // Google green outline
    ALEXA:  { background: '#03202c', color: '#8ae9ff', border: '2px solid #1fc7ff', fontWeight: 800 }, // Alexa cyan outline, bold
};

const CLIENT_ICONS = {
    GOOGLE: faGoogle,
    ALEXA:  faAmazon,
};

const ACTION_STYLES = {
    EXEC:   { background: '#6366f1', color: '#fff' },
    SYNC:   { background: '#a5b4fc', color: '#1e1b4b' },
    REPORT: { background: '#818cf8', color: '#1e1b4b' },
};

// ── Source cell (used by LiveStream.jsx) ──────────────────────────────────────

export function IntegrationsSourceCell({ row }) {
    const client    = (row.payload?.attributes?.integration_client || '').toUpperCase();
    const intAction = row.payload?.action;
    // EXEC and REPORT both carry a single bond_id
    const showBond    = intAction === 'EXEC' || intAction === 'REPORT';
    const intBondId   = showBond ? (row.payload?.attributes?.bond_id || null) : null;
    const intBondColor = intBondId ? getBondColor(intBondId) : null;
    const clientStyle  = CLIENT_STYLES[client]    || { background: '#6c757d', color: '#fff' };
    const actionStyle  = ACTION_STYLES[intAction] || { background: '#6c757d', color: '#fff' };

    return (
        <div className="small">
            <div className="d-flex w-100 gap-1 mb-1">
                <Badge className="integrationBadge" style={{ '--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.75rem', flex: 1, textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faUser} className="me-1" size="xs" />User
                </Badge>
                {intBondId && (
                    <Badge className="bondBadge" style={{ '--bond-badge-bg': intBondColor, color: '#ffffff', fontSize: '0.75rem', flex: 1, textAlign: 'center' }}>{intBondId}</Badge>
                )}
            </div>
            <div className="d-flex align-items-center w-100">
                <Badge className="integrationBadge" style={{ '--integration-badge-bg': clientStyle.background, '--integration-badge-color': clientStyle.color, border: clientStyle.border, fontWeight: clientStyle.fontWeight, fontSize: '0.75rem', flex: 1, textAlign: 'center' }}>
                    {CLIENT_ICONS[client] && <FontAwesomeIcon icon={CLIENT_ICONS[client]} className="me-1" size="xs" />}{client || 'INTEGRATION'}
                </Badge>
                <span style={{ flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d' }}>›</span>
                <Badge pill className="integrationBadge" style={{ '--integration-badge-bg': actionStyle.background, '--integration-badge-color': actionStyle.color, fontSize: '0.75rem', flex: 1, textAlign: 'center' }}>{intAction}</Badge>
            </div>
        </div>
    );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function getStatusVariant(code) {
    if (code == null) return 'secondary';
    if (code < 300) return 'success';
    if (code < 400) return 'warning';
    if (code < 600) return 'danger';
    return 'secondary';
}

function MonoChip({ children }) {
    return (
        <span style={{
            fontFamily: 'monospace', fontSize: '0.72rem', color: '#adb5bd',
            background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '1px 6px',
        }}>{children}</span>
    );
}

function JsonToggle({ show, onToggle, label = '{}' }) {
    return (
        <Badge
            bg={show ? 'secondary' : 'dark'}
            style={{ cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none', lineHeight: '1.5', padding: '0.35em 0.65em' }}
            onClick={onToggle}
            title="Toggle JSON"
        >{label}</Badge>
    );
}

function JsonPanel({ src }) {
    return (
        <div style={{ marginTop: '6px', maxWidth: '100%', overflowX: 'auto' }}>
            <ReactJsonView
                src={src}
                className="mqttEventPayload"
                theme="ocean"
                borderStyle="none"
                name={false}
                displayObjectSize={false}
                displayDataTypes={false}
                collapsed={2}
            />
        </div>
    );
}

// ── EXEC ──────────────────────────────────────────────────────────────────────
// Google: attrs has action, data, actions_api_response, wait_reply
// Alexa:  attrs has action (namespace), meta has alexa_payload/header/endpoint/response

function ExecEvent({ attrs, payload, bondsData }) {
    const [showJson, setShowJson] = useState(false);

    const { bond_id, device_id, action, data, actions_api_response } = attrs;
    const bondDevice = resolveBondDevice(bondsData, bond_id, device_id);
    const deviceName = bondDevice?.name || null;
    const hasData    = data && Object.keys(data).length > 0;
    // Alexa EXECs carry the concrete command (e.g. TurnOff) in meta.alexa_payload
    const alexaCommand = payload.meta?.alexa_payload?.name;
    const actionLabel  = action && alexaCommand ? `${action} - ${alexaCommand}` : action;

    return (
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Row 1: device name · action · response */}
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                {deviceName && (
                    <Badge bg="warning" text="dark" style={{ fontSize: '0.75rem' }}>{deviceName}</Badge>
                )}
                {actionLabel && (
                    <Badge bg="primary" style={{ fontSize: '0.75rem' }}>{actionLabel}</Badge>
                )}
                {hasData && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{JSON.stringify(data)}</Badge>
                )}
                {actions_api_response != null && (
                    <Badge bg={getStatusVariant(actions_api_response)} style={{ fontSize: '0.75rem' }}>{actions_api_response}</Badge>
                )}
            </div>

            {/* Row 2: {} toggle */}
            <div className="d-flex align-items-center gap-1 mt-1">
                <JsonToggle show={showJson} onToggle={() => setShowJson(s => !s)} />
            </div>

            {showJson && <JsonPanel src={payload} />}
        </div>
    );
}

// ── REPORT ──────────────────────────────────────────────────────────────────
// Alexa ReportState: bond_id, device_id, device_type, device_mode

function ReportEvent({ attrs, payload }) {
    const [showJson, setShowJson] = useState(false);
    const { bond_id, device_id, device_type, device_mode } = attrs;
    const bondColor = bond_id ? getBondColor(bond_id) : null;

    return (
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Row 1: bond · device_type · device_mode */}
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                {bond_id && (
                    <Badge className="bondBadge" style={{ '--bond-badge-bg': bondColor, color: '#fff', fontSize: '0.75rem' }}>{bond_id}</Badge>
                )}
                {device_type && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>{device_type}</Badge>
                )}
                {device_mode && (
                    <Badge bg="info" text="dark" style={{ fontSize: '0.75rem' }}>{device_mode}</Badge>
                )}
            </div>

            {/* Row 2: device_id · {} toggle */}
            <div className="d-flex align-items-center flex-wrap gap-1">
                {device_id && <MonoChip>{device_id}</MonoChip>}
                <JsonToggle show={showJson} onToggle={() => setShowJson(s => !s)} />
            </div>

            {showJson && <JsonPanel src={payload} />}
        </div>
    );
}

// ── SYNC ──────────────────────────────────────────────────────────────────────
// Google: attrs.devices_list — array of {name, type, traits, id}
// Alexa:  attrs.bond_id is a list of bond IDs; meta.alexa_response has discovery JSON

function resolveBondDevice(bondsData, bondId, deviceId) {
    if (!bondId || !deviceId || !bondsData) return null;
    const devices = bondsData[bondId]?.devices;
    if (!Array.isArray(devices)) return null;
    return devices.find(d =>
        d.device_id === deviceId ||
        parseInt(d.device_id, 16) === parseInt(deviceId, 16)
    ) || null;
}

function SyncEvent({ attrs, meta, payload, client, bondsData }) {
    const [showItems, setShowItems] = useState(false);
    const [showMeta,  setShowMeta]  = useState(false);
    const [showJson,  setShowJson]  = useState(false);

    const isAlexa   = client === 'ALEXA';
    const devices   = attrs.devices_list || [];
    const bondIds   = isAlexa && Array.isArray(attrs.bond_id) ? attrs.bond_id : [];
    const hasMeta   = meta && Object.keys(meta).length > 0;

    // For Alexa, prefer endpoint count from parsed alexa_response
    let alexaEndpoints = [];
    if (isAlexa && meta.alexa_response) {
        try {
            const parsed = JSON.parse(meta.alexa_response);
            alexaEndpoints = parsed?.event?.payload?.endpoints || [];
        } catch { /* ignore */ }
    }
    const itemCount = isAlexa ? (alexaEndpoints.length || bondIds.length) : devices.length;
    const itemLabel = isAlexa ? 'device' : 'device';

    return (
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Row 1: expand toggle · meta toggle · {} toggle */}
            <div className="d-flex align-items-center gap-1 mb-1">
                {itemCount > 0 && (
                    <JsonToggle
                        show={showItems}
                        onToggle={() => setShowItems(s => !s)}
                        label={showItems ? `▲ ${itemCount} ${itemLabel}s` : `▼ ${itemCount} ${itemLabel}s`}
                    />
                )}
                {hasMeta && (
                    <JsonToggle show={showMeta} onToggle={() => setShowMeta(s => !s)} label={showMeta ? '▲ meta' : '▼ meta'} />
                )}
                <JsonToggle show={showJson} onToggle={() => setShowJson(s => !s)} />
            </div>

            {/* Alexa: endpoint device list from meta.alexa_response */}
            {showItems && isAlexa && (() => {
                const endpoints = alexaEndpoints;

                if (endpoints.length > 0) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px' }}>
                            {endpoints.map((ep, i) => {
                                const cookie      = ep.cookie || {};
                                const bondId      = cookie.bond_id;
                                const deviceId    = cookie.device_id;
                                const bondDevice  = resolveBondDevice(bondsData, bondId, deviceId);
                                const bondColor   = bondId ? getBondColor(bondId) : null;
                                const category    = ep.displayCategories?.[0] || null;
                                const caps        = Array.isArray(ep.capabilities)
                                    ? ep.capabilities.map(c => c.interface?.replace(/^Alexa\./i, '') || '').filter(Boolean)
                                    : [];

                                return (
                                    <div key={i} className="d-flex align-items-center gap-1 w-100"
                                        style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '4px', padding: '3px 6px', flexWrap: 'nowrap', overflow: 'hidden' }}>
                                        {bondId && (
                                            <Badge className="bondBadge flex-shrink-0" style={{ '--bond-badge-bg': bondColor, color: '#fff', fontSize: '0.72rem', minWidth: '160px', textAlign: 'center' }}>{bondId}</Badge>
                                        )}
                                        {ep.friendlyName && (
                                            <Badge bg="warning" text="dark" className="flex-shrink-0" style={{ fontSize: '0.72rem', minWidth: '200px', textAlign: 'center' }}>{ep.friendlyName}</Badge>
                                        )}
                                        {category && (
                                            <Badge bg="secondary" className="flex-shrink-0" style={{ fontSize: '0.72rem', minWidth: '120px', textAlign: 'center' }}>{category}</Badge>
                                        )}
                                        {bondDevice?.name && (
                                            <Badge bg="dark" className="flex-shrink-0" style={{ fontSize: '0.72rem', border: '1px solid rgba(255,255,255,0.15)' }}>{bondDevice.name}</Badge>
                                        )}
                                        {caps.length > 0 && (
                                            <div className="d-flex gap-1 ms-auto flex-shrink-0 flex-wrap justify-content-end">
                                                {caps.map((cap, ci) => (
                                                    <Badge key={ci} bg="primary" style={{ fontSize: '0.68rem', opacity: 0.8 }}>{cap}</Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                }

                // Fallback: show raw bond ID badges if parsing failed
                return bondIds.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                        {bondIds.map((bid, i) => (
                            <Badge key={i} className="bondBadge" style={{ '--bond-badge-bg': getBondColor(bid), color: '#fff', fontSize: '0.75rem' }}>{bid}</Badge>
                        ))}
                    </div>
                ) : null;
            })()}

            {/* Google: device list rows */}
            {showItems && !isAlexa && devices.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px' }}>
                    {devices.map((device, i) => {
                        const cd         = device.customData || {};
                        const bondId     = cd.bond_id;
                        const deviceId   = cd.device_id;
                        const bondDevice = resolveBondDevice(bondsData, bondId, deviceId);
                        const googleName = device.name?.name ?? (typeof device.name === 'string' ? device.name : null);
                        const deviceType = device.type ? device.type.replace(/^action\.devices\.types\./i, '') : null;
                        const bondColor  = bondId ? getBondColor(bondId) : null;

                        return (
                            <div key={i} className="d-flex align-items-center gap-1 w-100"
                                style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '4px', padding: '3px 6px', flexWrap: 'nowrap', overflow: 'hidden' }}>
                                {bondId && (
                                    <Badge className="bondBadge flex-shrink-0" style={{ '--bond-badge-bg': bondColor, color: '#fff', fontSize: '0.72rem', minWidth: '160px', textAlign: 'center' }}>{bondId}</Badge>
                                )}
                                {googleName && (
                                    <Badge bg="warning" text="dark" className="flex-shrink-0" style={{ fontSize: '0.72rem', minWidth: '200px', textAlign: 'center' }}>{googleName}</Badge>
                                )}
                                {deviceType && (
                                    <Badge bg="secondary" className="flex-shrink-0" style={{ fontSize: '0.72rem', minWidth: '120px', textAlign: 'center' }}>{deviceType}</Badge>
                                )}
                                {bondDevice?.name && (
                                    <Badge bg="dark" className="flex-shrink-0" style={{ fontSize: '0.72rem', border: '1px solid rgba(255,255,255,0.15)' }}>{bondDevice.name}</Badge>
                                )}
                                {Array.isArray(device.traits) && device.traits.length > 0 && (
                                    <div className="d-flex gap-1 ms-auto flex-shrink-0 flex-wrap justify-content-end">
                                        {device.traits.map((trait, ti) => (
                                            <Badge key={ti} bg="primary" style={{ fontSize: '0.68rem', opacity: 0.8 }}>
                                                {typeof trait === 'string' ? trait.replace(/^action\.devices\.traits\./i, '') : JSON.stringify(trait)}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showMeta && hasMeta && <JsonPanel src={meta} />}
            {showJson && <JsonPanel src={payload} />}
        </div>
    );
}

// ── Dispatcher ────────────────────────────────────────────────────────────────

function IntegrationsEvent({ event, bondsData }) {
    const payload = event.payload || {};
    const action  = payload.action;
    const attrs   = payload.attributes || {};
    const meta    = payload.meta || {};
    const client  = (attrs.integration_client || '').toUpperCase();

    if (action === 'EXEC')   return <ExecEvent   attrs={attrs} payload={payload} bondsData={bondsData} />;
    if (action === 'REPORT') return <ReportEvent attrs={attrs} payload={payload} />;
    if (action === 'SYNC')   return <SyncEvent   attrs={attrs} meta={meta} payload={payload} client={client} bondsData={bondsData} />;

    // Unknown action — fall back to raw JSON
    return <SyncEvent attrs={attrs} meta={meta} payload={payload} client={client} bondsData={bondsData} />;
}

export default IntegrationsEvent;
